import { FormEvent, useState } from 'react';
import { signIn } from '@/lib/auth';

interface SignInProps {
  onSignedIn: () => void;
}

export function SignIn({ onSignedIn }: SignInProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await signIn({ username: email.trim(), password });
      if (result.isSignedIn) {
        onSignedIn();
        return;
      }
      setError(`Sign-in needs another step: ${result.nextStep.signInStep}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-in failed.';
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <form
        onSubmit={onSubmit}
        className="card-base w-full max-w-md p-9 flex flex-col gap-5"
      >
        <div className="flex flex-col gap-1">
          <span className="text-eyebrow uppercase tracking-[0.18em] text-inkFaded font-medium">
            The Daily
          </span>
          <h1 className="font-serif italic text-display-sm text-ink">
            Good to see you.
          </h1>
          <p className="text-body text-inkSoft">
            Sign in to open the wall.
          </p>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-caption uppercase tracking-[0.12em] text-inkFaded">
            Email
          </span>
          <input
            type="email"
            autoComplete="email"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-cardWarm border border-inkGhost/40 rounded-sm px-3 py-2 text-body-lg font-sans text-ink focus:outline-none focus:border-tomato focus:ring-2 focus:ring-tomato/20"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-caption uppercase tracking-[0.12em] text-inkFaded">
            Password
          </span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-cardWarm border border-inkGhost/40 rounded-sm px-3 py-2 text-body-lg font-sans text-ink focus:outline-none focus:border-tomato focus:ring-2 focus:ring-tomato/20"
          />
        </label>

        {error && (
          <p className="text-body-sm text-tomatoDeep font-sans">{error}</p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="bg-tomato hover:bg-tomatoDeep disabled:bg-inkFaded text-card rounded-sm px-4 py-3 text-body-lg font-sans font-medium transition-colors"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
